import { Collapse, Grid } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useSetRecoilState } from "recoil";
import { FormAutocomplete, FormDatePicker, FormDateTimePicker, FormTextField } from "../../../../components/fields";
import FilterActions from "../../../../components/filter/filter-actions/FilterActions";
import FilterWrapper from "../../../../components/filter/filter-wrapper/FilterWrapper";
import { RuleStatus, RuleType } from "../../../../types";
import { LOCALE_DATE_FORMAT } from "../../../../utils";
import { rulesFilterState } from "../../recoil";
import { IFormValuesRulesFilter } from "../../types";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import TenantForm from "../../../../components/multitenant/TenantForm";
import DealerForm from "../../../../components/multitenant/DealerForm";

const FilterRules = () => {

    const { t } = useTranslation('booking');
    const { globalData } = useContext(GlobalContext);

    const setRulesFilter = useSetRecoilState(rulesFilterState);
    const userHasMultipleTenants = globalData?.user.legalEntities.length > 1
    const userHasMultipleDealers = globalData?.user.dealers.length > 1

    const validationSchema = yup.object({
        creationDateFrom: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testCreationDateFrom",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.creationDateTo ||
                        value <= context.parent.creationDateTo
                    );
                }
            ),
        creationDateTo: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testCreationDateTo",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.creationDateFrom ||
                        value >= context.parent.creationDateFrom
                    );
                }
            ),
        startDateTimeFrom: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testStartDateTimeFrom",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.startDateTimeTo ||
                        value <= context.parent.startDateTimeTo
                    );
                }
            ),
        startDateTimeTo: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testStartDateTimeTo",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.startDateTimeFrom ||
                        value <= context.parent.startDateTimeFrom
                    );
                }
            ),
        endDateTimeFrom: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testEndDateTimeFrom",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.endDateTimeTo ||
                        value >= context.parent.endDateTimeTo
                    );
                }
            ),
        endDateTimeTo: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testEndDateTimeTo",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.endDateTimeFrom ||
                        value >= context.parent.endDateTimeFrom
                    );
                }
            ),
        tenants: yup
        .array()
        .min(1, "message.validation.required")
    });

    const form = useForm<IFormValuesRulesFilter>({
        defaultValues: {
            status: {
                value: RuleStatus.Active,
                label: 'Active'
            },
            type: null,
            name: '',
            creationDateFrom: null,
            creationDateTo: null,
            user: '',
            startDateTimeFrom: null,
            startDateTimeTo: null,
            endDateTimeFrom: null,
            endDateTimeTo: null,
            tenants: globalData?.user.legalEntities?.map((elem: any) => elem.name),
            dealers: globalData?.user.dealers?.map((elem: any) => elem.name)
        },
        resolver: yupResolver(validationSchema, {}, { rawValues: true })
    });

    const tenantsWatch = useWatch<IFormValuesRulesFilter, 'tenants'>({
            control: form.control,
            name: "tenants"
        });

    const [expanded, setExpanded] = useState<boolean>(false);

    const createRulesFilter = useCallback((data: IFormValuesRulesFilter) => ({
        status: data.status?.value,
        type: data.type?.value,
        name: data.name || undefined,
        creationDateFrom: data.creationDateFrom?.format(LOCALE_DATE_FORMAT),
        creationDateTo: data.creationDateTo?.format(LOCALE_DATE_FORMAT),
        user: data.user || undefined,
        startDateTimeFrom: data.startDateTimeFrom?.toISOString(),
        startDateTimeTo: data.startDateTimeTo?.toISOString(),
        endDateTimeFrom: data.endDateTimeFrom?.toISOString(),
        endDateTimeTo: data.endDateTimeTo?.toISOString(),
        tenants: data.tenants || [],
        dealers: data.dealers
    }), []);
    const handleSearch = useCallback((data: IFormValuesRulesFilter) => {
        setRulesFilter(createRulesFilter(data));
        setExpanded(false);
    }, []);
    const handleReset = useCallback(() => {
        form.reset();
        setRulesFilter(createRulesFilter(form.getValues()));
        setExpanded(false);
    }, []);
    const handleToggle = useCallback(() => {
        setExpanded(current => !current);
    }, [setExpanded]);

    useEffect(() => {
        setRulesFilter(createRulesFilter(form.getValues()));
    }, []);

    return (
        <FilterWrapper>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(handleSearch)}>
                    <Grid container columnSpacing={5} alignItems="center">
                        {userHasMultipleTenants &&
                            <Grid item xs={12} md={6} lg={3}>
                                <TenantForm
                                    control={form.control}
                                    name="tenants"
                                    onChange={() => userHasMultipleDealers
                                        ? form.setValue("dealers", [])
                                        : form.setValue("dealers", globalData?.user.dealers?.map((elem: any) => elem.name))
                                    }
                                />
                            </Grid>
                        }
                        {userHasMultipleDealers &&
                            <Grid item xs={12} md={6} lg={3}>
                                <DealerForm
                                    control={form.control}
                                    name="dealers"
                                    tenants={tenantsWatch}
                                />
                            </Grid>
                        }
                    </Grid>
                    <Grid container columnSpacing={5} alignItems="center">
                        {userHasMultipleTenants &&
                            <Grid item xs={12} md={6} lg={3}>
                                <TenantForm
                                    control={form.control}
                                    name="tenants"
                                    onChange={() => userHasMultipleDealers
                                        ? form.setValue("dealers", [])
                                        : form.setValue("dealers", globalData?.user.dealers?.map((elem: any) => elem.name))
                                    }
                                />
                            </Grid>
                        }
                        {userHasMultipleDealers &&
                            <Grid item xs={12} md={6} lg={3}>
                                <DealerForm
                                    control={form.control}
                                    name="dealers"
                                    tenants={tenantsWatch}
                                />
                            </Grid>
                        }
                    </Grid>
                    <Grid container columnSpacing={5} alignItems="center">
                        <Grid item xs={12} md={6} lg={3}>
                            <FormAutocomplete<IFormValuesRulesFilter>
                                name="status"
                                control={form.control}
                                label={t('label.status')}
                                options={[
                                    {
                                        label: t('label.active'),
                                        value: RuleStatus.Active
                                    },
                                    {
                                        label: t('label.notActive'),
                                        value: RuleStatus.NotActive
                                    },
                                    {
                                        label: t('label.onHold'),
                                        value: RuleStatus.OnHold
                                    }
                                ]}
                                isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <FormAutocomplete<IFormValuesRulesFilter>
                                name="type"
                                control={form.control}
                                label={t('label.type')}
                                options={[
                                    {
                                        label: t('label.pending'),
                                        value: RuleType.Pending
                                    },
                                    {
                                        label: t('label.stopSell'),
                                        value: RuleType.StopSells
                                    }
                                ]}
                                isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <FormTextField<IFormValuesRulesFilter>
                                name="name"
                                control={form.control}
                                label={t('label.name')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <FilterActions
                                expanded={expanded}
                                onSearch={form.handleSubmit(handleSearch)}
                                onReset={handleReset}
                                onToggle={handleToggle}
                            />
                        </Grid>
                    </Grid>
                    <Collapse in={expanded} timeout="auto" sx={{ width: '100%' }}>
                        <Grid container columnSpacing={5} rowSpacing={2} pt={2}>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IFormValuesRulesFilter>
                                    name="startDateTimeFrom"
                                    control={form.control}
                                    label={t('label.startDateTimeFrom')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IFormValuesRulesFilter>
                                    name="startDateTimeTo"
                                    control={form.control}
                                    label={t('label.startDateTimeTo')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IFormValuesRulesFilter>
                                    name="endDateTimeFrom"
                                    control={form.control}
                                    label={t('label.endDateTimeFrom')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IFormValuesRulesFilter>
                                    name="endDateTimeTo"
                                    control={form.control}
                                    label={t('label.endDateTimeTo')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDatePicker<IFormValuesRulesFilter>
                                    name="creationDateFrom"
                                    control={form.control}
                                    label={t('label.creationDateFrom')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDatePicker<IFormValuesRulesFilter>
                                    name="creationDateTo"
                                    control={form.control}
                                    label={t('label.creationDateTo')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IFormValuesRulesFilter>
                                    name="user"
                                    control={form.control}
                                    label={t('label.user')}
                                />
                            </Grid>
                        </Grid>
                    </Collapse>
                </form>
            </FormProvider>
        </FilterWrapper>
    )
};

export default FilterRules;
